// Sound absorption in seawater — Francois & Garrison (1982), the model Les Bender's
// deliverable prints at 2/4/8 kHz (the sub-bottom bands his survey work ensonifies).
// His own AbsorptionCoefficients.m was missing from the code drop; Cody approved F&G
// as the replacement (2026-07-21). This is the acoustic counterpart to soundspeed.ts:
// where that gives how FAST sound travels, this gives how fast it FADES.
//
// Reference — two-part paper, cited together because the total-absorption equation
// lives in Part II but leans on Part I's magnesium-sulphate relaxation:
//   Francois, R.E. & Garrison, G.R., "Sound absorption based on ocean measurements.
//     Part I: Pure water and magnesium sulfate contributions",
//     J. Acoust. Soc. Am. 72(3), 896–907 (1982).
//   Francois, R.E. & Garrison, G.R., "Sound absorption based on ocean measurements.
//     Part II: Boric acid contribution and equation for total absorption",
//     J. Acoust. Soc. Am. 72(6), 1879–1890 (1982).
// Valid roughly 200 Hz < f < 1 MHz across oceanic T/S; the sub-bottom bands we print
// sit comfortably inside that. Method cross-checked against the NPL "Calculation of
// absorption of sound in seawater" technical guide, which implements the same model.
//
// Total absorption is the sum of three relaxation/viscous mechanisms, each a term of
// the form A·P·f_r·f² / (f_r² + f²) (a relaxation absorber) or A·P·f² (viscous):
//   1. boric acid  B(OH)₃  — dominates below ~1 kHz
//   2. magnesium sulphate  MgSO₄ — dominates ~1–100 kHz
//   3. pure water (shear viscosity) — dominates above ~100 kHz
// f is in kHz throughout and the result is dB/km.

/**
 * Absorption coefficient, dB/km, per Francois & Garrison (1982).
 *
 * @param tempC       temperature, °C
 * @param salPSU      salinity, PSU
 * @param depthM      depth, m (drives the pressure corrections P2, P3)
 * @param pH          acidity; F&G's own default in SVProfile_Output.m is 8.0, and the
 *                    boric-acid term is the only pH-sensitive one (∝ 10^(0.78·pH))
 * @param freqKhz     ensonification frequency, kHz
 * @param soundSpeedMs speed of sound, m/s. Passed in — NOT recomputed here — to match
 *                    Les's AbsorptionCoefficients(D, c, T, Sal, pH, f) signature, which
 *                    takes the profile's own sound speed. The boric-acid (A1) and
 *                    magnesium-sulphate (A2) amplitudes both scale as 1/c. NOTE: F&G's
 *                    published curves use their own internal simplified sound speed,
 *                    c = 1412 + 3.21·T + 1.19·S + 0.0167·z; feeding that exact c
 *                    reproduces their check values, which is what absorption.test.ts
 *                    does. Feeding the surveyor's selected-equation SV instead (what
 *                    the panel does) shifts the amplitudes by ~c's few-per-mille spread
 *                    — deliberate: the deliverable should be self-consistent with the
 *                    sound speed printed in the adjacent column.
 */
export function francoisGarrison(
  tempC: number,
  salPSU: number,
  depthM: number,
  pH: number,
  freqKhz: number,
  soundSpeedMs: number,
): number {
  const T = tempC;
  const S = salPSU;
  const z = depthM;
  const c = soundSpeedMs;
  const f = freqKhz;
  // Absolute temperature drives both relaxation frequencies (Arrhenius-style terms).
  const theta = T + 273.0;

  // --- 1. Boric acid B(OH)₃ (Part II, eqs. for A1, f1) --------------------------
  // A1 carries the pH dependence: 10^(0.78·pH − 5). f1 is the boric relaxation
  // frequency, rising with salinity (√(S/35)) and temperature. P1 = 1 (F&G found no
  // measurable pressure dependence for the boric term).
  const A1 = (8.686 / c) * Math.pow(10, 0.78 * pH - 5.0);
  const P1 = 1.0;
  const f1 = 2.8 * Math.sqrt(S / 35.0) * Math.pow(10, 4.0 - 1245.0 / theta);
  const boric = (A1 * P1 * f1 * f * f) / (f1 * f1 + f * f);

  // --- 2. Magnesium sulphate MgSO₄ (Part I, eqs. for A2, f2; Part II P2) --------
  // A2 scales with salinity and (weakly) temperature; f2 is the MgSO₄ relaxation
  // frequency (tens of kHz). P2 is the pressure (depth) correction, which slightly
  // REDUCES this term with depth.
  const A2 = 21.44 * (S / c) * (1.0 + 0.025 * T);
  const P2 = 1.0 - 1.37e-4 * z + 6.2e-9 * z * z;
  const f2 = (8.17 * Math.pow(10, 8.0 - 1990.0 / theta)) / (1.0 + 0.0018 * (S - 35.0));
  const mgso4 = (A2 * P2 * f2 * f * f) / (f2 * f2 + f * f);

  // --- 3. Pure water, shear viscosity (Part I; A3 branches at 20 °C) ------------
  // A3 is a quartic in T with DIFFERENT coefficients above and below 20 °C — F&G fit
  // the two regimes separately. Getting the threshold or a coefficient wrong here is
  // exactly the kind of transcription slip absorption.test.ts guards against. This
  // term is viscous (∝ f², no relaxation frequency); P3 is its pressure correction.
  const A3 =
    T <= 20.0
      ? 4.937e-4 - 2.59e-5 * T + 9.11e-7 * T * T - 1.50e-8 * T * T * T
      : 3.964e-4 - 1.146e-5 * T + 1.45e-7 * T * T - 6.5e-10 * T * T * T;
  const P3 = 1.0 - 3.83e-5 * z + 4.9e-10 * z * z;
  const water = A3 * P3 * f * f;

  return boric + mgso4 + water;
}

/** The sub-bottom bands Les's deliverable prints (SVProfile_Output.m: f = 2, 4, 8 kHz). */
export const ABSORPTION_FREQS_KHZ = [2, 4, 8] as const;

/** pH used for absorption when the console has no measured value — F&G's own default,
 *  and the literal value in Les's SVProfile_Output.m (`pH = 8.0`). Seawater sits in
 *  the 7.6–8.2 band F&G validated; 8.0 is the open-ocean midpoint. */
export const DEFAULT_PH = 8.0;
